import PropTypes from 'prop-types';
import { memo, useCallback, useMemo, useState } from 'react';

import PopperTooltip from 'shared/ui/Popper/PopperTooltip';
import { attachmentTypeCodes } from 'shared/constants';
// eslint-disable-next-line import/no-cycle
import ModalUploadFile, {
  buildOptionFromFileType,
  FormUploadFileValues,
  ModalUploadFileOptions,
} from 'shared/ui/Modal/ModalUploadFile';
import { BaseAttachmentDto, BaseFileTypeDto } from '@eva/types/dto';

import { getFileName, getFileTypeCode, getFileTypeId, getFileTypeIdOfFile } from 'containers/FilesTable';

import { UploadFileBaseValues } from '../Modal/types';

const styles = { fontSize: '14px' };

export interface FileButtonEditFileProps<T, FT> {
  file: T;
  fileTypes: FT[];
  onSubmit: <FV extends UploadFileBaseValues>(file: T, values: FV) => Promise<T>;
}

const FileButtonEditFile = <T extends BaseAttachmentDto, FT extends BaseFileTypeDto>({
  file,
  fileTypes,
  onSubmit,
}: FileButtonEditFileProps<T, FT>) => {
  const isCandidate = window.isCandidate;

  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const options: ModalUploadFileOptions<FT> = useMemo(() => {
    const noType = fileTypes.find((ft) => getFileTypeCode(ft) === attachmentTypeCodes.noType);

    const currentFileType = fileTypes.find((ft) => getFileTypeId(ft) === getFileTypeIdOfFile(file));

    const fileType = !currentFileType || currentFileType?.disabled ? noType : currentFileType;

    return {
      fileTypes,
      updateMode: true,
      canVerify: !isCandidate,
      initialValues: {
        verified: file.verified,
        title: file.title || file.filename,
        files: [{ name: getFileName(file) }],
        fileType: buildOptionFromFileType(fileType),
        expiryDate: file.expiryDate?.substring(0, 10) || '',
        issueDate: file.issueDate?.substring(0, 10) || '',
      },
    };
  }, [isCandidate, file, fileTypes]);

  const toggleUploadDialog = useCallback(() => {
    setShowUploadDialog(!showUploadDialog);
  }, [showUploadDialog]);

  const handleSubmit = useCallback((values: FormUploadFileValues) => onSubmit(file, values), [file, onSubmit]);

  if (isCandidate && file.verified) {
    return null;
  }

  return (
    <span>
      <ModalUploadFile options={options} show={showUploadDialog} onHide={toggleUploadDialog} onSubmit={handleSubmit} />

      <PopperTooltip placement="top" overlay={translate('Edit')}>
        <button
          type="button"
          className="btn btn-xs btn-box-tool no-padding"
          style={styles}
          onClick={toggleUploadDialog}
        >
          <i className="lnr lnr-pencil" />
        </button>
      </PopperTooltip>
    </span>
  );
};

FileButtonEditFile.contextTypes = {
  isAllowedOperation: PropTypes.func.isRequired,
};

FileButtonEditFile.propTypes = {
  file: PropTypes.object.isRequired,
  fileTypes: PropTypes.array.isRequired,
  isCandidate: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-default-export
export default memo(FileButtonEditFile);
